import React, { useState } from 'react';
import type { ReviewType } from '@wineryxplorer/shared';
import { trpc } from '../../../lib/trpc';
import { ModerationCard, type ModerationReview } from './ModerationCard';
import { LoadingSpinner } from '../../ui/LoadingSpinner';

export type ModerationTab = 'venueManager' | 'community';

export interface ModerationQueueProps {
  className?: string;
}

/**
 * Tab view for venue manager vs community flagged reviews.
 * Requirements: 9.1, 10.1
 */
export const ModerationQueue = ({ className = '' }: ModerationQueueProps) => {
  const [activeTab, setActiveTab] = useState<ModerationTab>('venueManager');
  const [processingReviewId, setProcessingReviewId] = useState<string | null>(null);
  const utils = trpc.useUtils();

  // Fetch venue manager flagged reviews
  const {
    data: venueManagerData,
    isLoading: isLoadingVenueManager,
    error: venueManagerError,
    refetch: refetchVenueManager,
  } = trpc.moderation.venueManagerQueue.useQuery();

  // Fetch community flagged reviews
  const {
    data: communityData,
    isLoading: isLoadingCommunity,
    error: communityError,
    refetch: refetchCommunity,
  } = trpc.moderation.communityQueue.useQuery();

  // Resolve venue manager flag mutation
  const resolveVenueManagerFlag = trpc.moderation.resolveVenueManagerFlag.useMutation({
    onSuccess: () => {
      refetchVenueManager();
      utils.moderation.pendingCount.invalidate();
    },
  });

  // Resolve community flag mutation
  const resolveCommunityFlag = trpc.moderation.resolveCommunityFlag.useMutation({
    onSuccess: () => {
      refetchCommunity();
      utils.moderation.pendingCount.invalidate();
    },
  });

  const handleApprove = async (reviewId: string, reviewType: ReviewType, reason: string) => {
    setProcessingReviewId(reviewId);
    try {
      if (activeTab === 'venueManager') {
        await resolveVenueManagerFlag.mutateAsync({
          reviewId,
          reviewType,
          decision: 'approved',
          reason,
        });
      } else {
        await resolveCommunityFlag.mutateAsync({
          reviewId,
          reviewType,
          decision: 'approved',
          reason,
        });
      }
    } finally {
      setProcessingReviewId(null);
    }
  };

  const handleRemove = async (reviewId: string, reviewType: ReviewType, reason: string) => {
    setProcessingReviewId(reviewId);
    try {
      if (activeTab === 'venueManager') {
        await resolveVenueManagerFlag.mutateAsync({
          reviewId,
          reviewType,
          decision: 'removed',
          reason,
        });
      } else {
        await resolveCommunityFlag.mutateAsync({
          reviewId,
          reviewType,
          decision: 'removed',
          reason,
        });
      }
    } finally {
      setProcessingReviewId(null);
    }
  };

  const isLoading = activeTab === 'venueManager' ? isLoadingVenueManager : isLoadingCommunity;
  const error = activeTab === 'venueManager' ? venueManagerError : communityError;
  const reviews: ModerationReview[] =
    activeTab === 'venueManager'
      ? (venueManagerData?.items ?? [])
      : (communityData?.items ?? []);
  const totalCount =
    activeTab === 'venueManager'
      ? (venueManagerData?.total ?? 0)
      : (communityData?.total ?? 0);

  const venueManagerCount = venueManagerData?.total ?? 0;
  const communityCount = communityData?.total ?? 0;

  return (
    <div className={`bg-gray-50 rounded-lg shadow ${className}`}>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px" aria-label="Moderation queue tabs">
          <button
            type="button"
            onClick={() => setActiveTab('venueManager')}
            className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'venueManager'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-selected={activeTab === 'venueManager'}
            role="tab"
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              Venue Manager Flags
              {venueManagerCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {venueManagerCount}
                </span>
              )}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('community')}
            className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'community'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-selected={activeTab === 'community'}
            role="tab"
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
              </svg>
              Community Flags
              {communityCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {communityCount}
                </span>
              )}
            </span>
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load reviews</h3>
            <p className="text-gray-500 mb-4">
              {error.message || 'An error occurred while loading the moderation queue.'}
            </p>
            <button
              type="button"
              onClick={() => activeTab === 'venueManager' ? refetchVenueManager() : refetchCommunity()}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && reviews.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
            <p className="text-gray-500">
              {activeTab === 'venueManager'
                ? 'No reviews flagged by venue managers.'
                : 'No reviews flagged by the community.'}
            </p>
          </div>
        )}

        {/* Reviews List */}
        {!isLoading && !error && reviews.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {totalCount} {totalCount === 1 ? 'review' : 'reviews'} pending moderation
              </p>
            </div>
            {reviews.map((review) => (
              <ModerationCard
                key={review._id}
                review={review}
                flagType={activeTab}
                onApprove={handleApprove}
                onRemove={handleRemove}
                isProcessing={processingReviewId === review._id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
